import React from 'react';
import { useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AddLinkIcon from '@mui/icons-material/AddLink';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';

import usePdvAccess from '@giro-pdv/hooks/usePdvAccess.hook';
import useIsMobileHook from '@giro-pdv/hooks/useIsMobile.hook';

import { createInitialForm } from '../constants/paymentLinks.constants';
import { SummaryCards } from '../components/SummaryCards.component';
import { LinkFilters } from '../components/LinkFilters.component';
import { LinksList } from '../components/LinksList.component';
import { CreateLinkForm } from '../components/CreateLinkForm.component';
import { LinkFeeSimulation } from '../components/LinkFeeSimulation.component';
import { LinkDetailDialog } from '../components/LinkDetailDialog.component';
import { SendLinkDialog } from '../components/SendLinkDialog.component';
import { LinkActionsMenu } from '../components/LinkActionsMenu.component';
import usePaymentLinks from '../hooks/usePaymentLinks.hook';

const PaymentLinksScreen = () => {
  const location = useLocation();
  const isMobile = useIsMobileHook();
  const { canCreateBoleto } = usePdvAccess();
  // Abaixo de "lg" a tabela fica apertada (PDV também é usado como caixa); usamos cartões.
  const isCompact = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));

  const {
    actionMenu,
    clearFilters,
    closeActionMenu,
    copyLink,
    createLink,
    creating,
    destinationHint,
    detail,
    detailLoading,
    detailOpen,
    downloadLinkSummary,
    downloadQrCode,
    exportCsv,
    filters,
    filteredLinks,
    form,
    hasActiveFilters,
    links,
    loading,
    loadingMore,
    loadMoreLinks,
    menuItem,
    menuItemCanSend,
    menuItemIsFinal,
    openActionMenu,
    openCheckout,
    openDetail,
    openSendDialog,
    page,
    pageCount,
    pagedLinks,
    quickFilterItems,
    quickStatusGroupKey,
    quickFilterKey,
    refreshLinks,
    refreshStatus,
    refreshingId,
    rowsPerPage,
    runAction,
    searchInput,
    sendDialog,
    sendForm,
    sending,
    sendLink,
    closeDetail,
    setForm,
    setPage,
    setRowsPerPage,
    setSearchInput,
    setSendDialog,
    setSendForm,
    setTab,
    summaryGroups,
    tab,
    totalAvailableLinks,
    updateFilter,
    updateForm,
    applySummaryFilter,
    applyQuickFilter,
  } = usePaymentLinks();

  React.useEffect(() => {
    setTab('list');
  }, [location.key, setTab]);
  const summaryActiveKey =
    quickStatusGroupKey ||
    (filters.status === 'open'
      ? 'waiting'
      : filters.status === 'expired'
      ? 'failed'
      : filters.status === 'paid'
      ? 'paid'
      : '');

  return (
    <Box display="flex" flexDirection="column" gap={1.5} width="100%">
      <Box borderBottom="1px solid" borderColor="divider">
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ minHeight: 36 }}
        >
          <Tab
            icon={<FormatListBulletedIcon fontSize="small" />}
            iconPosition="start"
            label="Links"
            value="list"
            sx={{ minHeight: 36, py: 0.5 }}
          />
          <Tab
            icon={<AddLinkIcon fontSize="small" />}
            iconPosition="start"
            label={
              <Box
                component="span"
                display="inline-flex"
                alignItems="center"
                gap={0.5}
              >
                Criar link
                <Tooltip title="Crie, acompanhe e reenvie links de pagamento. O cliente recebe uma página para pagar por cartão ou Pix.">
                  <HelpOutlineIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
            }
            value="create"
            sx={{ minHeight: 36, py: 0.5 }}
          />
        </Tabs>
      </Box>

      <Box display="flex" flexDirection="column">
        {tab === 'list' ? (
          <Box
            display="flex"
            flex={1}
            flexDirection="column"
            gap={0.7}
            minHeight={0}
            px={{ xs: 0, md: 1 }}
            py={{ xs: 0, md: 0.45 }}
          >
            <Box
              alignItems="center"
              display="flex"
              flexWrap="wrap"
              gap={1}
              justifyContent="flex-end"
            >
              <Button
                disabled={loading || !filteredLinks.length}
                size="small"
                startIcon={<FileDownloadIcon />}
                variant="outlined"
                onClick={exportCsv}
                sx={{
                  borderColor: 'divider',
                  fontWeight: 700,
                  minHeight: 40,
                  px: 2,
                }}
              >
                {totalAvailableLinks > links.length
                  ? 'Exportar carregados'
                  : 'Exportar'}
              </Button>
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => setTab('create')}
                sx={{ fontWeight: 700, minHeight: 40, px: 2 }}
              >
                Criar link
              </Button>
            </Box>

            <SummaryCards
              activeKey={summaryActiveKey}
              groups={summaryGroups}
              onSelect={applySummaryFilter}
            />

            <LinksList
              filters={
                <LinkFilters
                  searchInput={searchInput}
                  setSearchInput={setSearchInput}
                  filters={filters}
                  quickFilterItems={quickFilterItems}
                  updateFilter={updateFilter}
                  clearFilters={clearFilters}
                  hasActiveFilters={hasActiveFilters}
                  onQuickFilter={applyQuickFilter}
                  onRefresh={refreshLinks}
                  quickFilterKey={quickFilterKey}
                  refreshing={loading}
                />
              }
              links={filteredLinks}
              loadedLinkCount={links.length}
              pagedLinks={pagedLinks}
              loading={loading}
              isCompact={isCompact}
              page={page}
              pageCount={pageCount}
              rowsPerPage={rowsPerPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
              onCopy={copyLink}
              onDetail={openDetail}
              onMenuOpen={openActionMenu}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
              onCreate={() => setTab('create')}
              onLoadMore={loadMoreLinks}
              loadingMore={loadingMore}
              totalAvailableLinks={totalAvailableLinks}
            />
          </Box>
        ) : (
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: 'minmax(0, 1fr)',
              lg: 'minmax(0, 1fr) 500px',
            }}
            alignItems="start"
            gap={2}
            width="100%"
            maxWidth={1440}
            mx="auto"
          >
            <CreateLinkForm
              canCreateBoleto={canCreateBoleto}
              form={form}
              updateForm={updateForm}
              onClear={() => setForm(createInitialForm())}
              onSubmit={createLink}
              creating={creating}
              destinationHint={destinationHint}
            />
            <LinkFeeSimulation amount={Number(form.amount)} type={form.type} />
          </Box>
        )}
      </Box>

      <LinkDetailDialog
        open={detailOpen}
        detail={detail}
        detailLoading={detailLoading}
        refreshingId={refreshingId}
        fullScreen={isMobile}
        onClose={closeDetail}
        onCopy={copyLink}
        onOpenCheckout={openCheckout}
        onRefresh={refreshStatus}
        onSend={openSendDialog}
        onDownloadSummary={downloadLinkSummary}
        onDownloadQr={downloadQrCode}
      />

      <SendLinkDialog
        open={Boolean(sendDialog)}
        sendForm={sendForm}
        setSendForm={setSendForm}
        sending={sending}
        onClose={() => setSendDialog(null)}
        onSend={sendLink}
      />

      <LinkActionsMenu
        anchorEl={actionMenu.anchorEl}
        item={menuItem}
        canSend={menuItemCanSend}
        isFinal={menuItemIsFinal}
        onClose={closeActionMenu}
        runAction={runAction}
        onCopy={copyLink}
        onOpenCheckout={openCheckout}
        onSend={openSendDialog}
        onRefresh={refreshStatus}
      />
    </Box>
  );
};

export default PaymentLinksScreen;
